import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import { motion } from 'framer-motion';
import { CARD_CLASS, OMS_COLORS, baseChartOptions, sectionVariants, tint } from '../../dashboard/omsDashTheme';
import { ADMIN, ADMIN_EYEBROW_CLASS } from '../adminTheme';
import { formatNumber, formatPct } from '../../dashboard/utils/formatNumber';
import type { AdoptionPoint, AdoptionSummary, CohortRow, FunnelStage, Granularity } from '../utils/adminMetrics';

interface Props {
  points: AdoptionPoint[];
  prevPoints: AdoptionPoint[];
  summary: AdoptionSummary;
  prevSummary: AdoptionSummary;
  funnel: FunnelStage[];
  cohorts: CohortRow[];
  granularity: Granularity;
  compare: boolean;
  phrase: string;
  loading: boolean;
}

const ACTIVE_LABEL: Record<Granularity, string> = {
  daily: 'Daily active engineers (DAU)',
  weekly: 'Weekly active engineers (WAU)',
  monthly: 'Monthly active engineers (MAU)',
};

function stickinessBand(value: number): string {
  if (value >= 0.5) return 'Habitual';
  if (value >= 0.25) return 'Regular';
  if (value >= 0.1) return 'Occasional';
  return 'Sporadic';
}

/** Cohort cell — value always printed, colour only reinforces. */
function CohortCell({ value }: { value: number | null }) {
  if (value === null) {
    return (
      <td className="px-1.5 py-1 text-center text-[10px] font-semibold" style={{ color: OMS_COLORS.caption }} title="Outside the fetched activity window">
        —
      </td>
    );
  }
  const alpha = 0.06 + (value / 100) * 0.3;
  return (
    <td
      className="px-1.5 py-1 text-center text-[10px] font-extrabold tabular-nums"
      style={{ color: '#0B3B2E', backgroundColor: `rgba(20,96,107,${alpha.toFixed(2)})` }}
    >
      {value}%
    </td>
  );
}

/**
 * Adoption — active-engineer trend (re-buckets to the granularity toggle),
 * stickiness with a band label, the activation funnel and the retention
 * cohort grid. An "app action" is a task status update — the tracked proxy
 * for app usage until session events exist.
 */
export default function AdoptionPanel({
  points,
  prevPoints,
  summary,
  prevSummary,
  funnel,
  cohorts,
  granularity,
  compare,
  phrase,
  loading,
}: Props) {
  const series: ApexAxisChartSeries = [
    { name: ACTIVE_LABEL[granularity], data: points.map((p) => p.active) },
  ];
  if (compare && prevPoints.length > 0) {
    series.push({
      name: `Previous window`,
      data: points.map((_, i) => prevPoints[i]?.active ?? 0),
    });
  }
  const options: ApexOptions = {
    ...baseChartOptions,
    chart: { ...baseChartOptions.chart, type: 'area' },
    colors: [ADMIN.petrol, OMS_COLORS.slate],
    stroke: { curve: 'smooth', width: [2.5, 2], dashArray: [0, 6] },
    fill: { type: 'gradient', gradient: { opacityFrom: 0.25, opacityTo: 0, stops: [0, 95] } },
    xaxis: {
      categories: points.map((p) => p.label),
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: { style: { colors: OMS_COLORS.caption, fontSize: '10px', fontWeight: 600 }, hideOverlappingLabels: true, rotate: 0 },
    },
    yaxis: {
      min: 0,
      forceNiceScale: true,
      title: { text: 'Unique engineers', style: { color: OMS_COLORS.caption, fontSize: '9px', fontWeight: 700 } },
      labels: { formatter: (v: number) => String(Math.round(v)), style: { colors: OMS_COLORS.caption, fontSize: '10px' } },
    },
    grid: { borderColor: 'rgba(11,59,46,0.08)', xaxis: { lines: { show: false } } },
    legend: { ...baseChartOptions.legend, position: 'bottom' },
    tooltip: { theme: 'light', shared: true, intersect: false },
  };

  const wauDelta = summary.wau - prevSummary.wau;

  return (
    <motion.section
      variants={sectionVariants}
      initial="hidden"
      animate="visible"
      custom={4}
      aria-label="Mobile app adoption"
      className={`${CARD_CLASS} p-4`}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className={ADMIN_EYEBROW_CLASS}>Adoption · mobile app usage</p>
        <div className="flex flex-wrap gap-x-5 gap-y-1 text-[11px] font-semibold" style={{ color: OMS_COLORS.caption }}>
          <span title="Unique engineers with ≥1 app action in the last 7 days of the window">
            WAU{' '}
            <span className="font-extrabold tabular-nums" style={{ color: OMS_COLORS.tealDark }}>
              {formatNumber(summary.wau)}
            </span>{' '}
            <span className="font-extrabold tabular-nums" style={{ color: wauDelta >= 0 ? '#1F5A47' : '#9F1239' }}>
              ({wauDelta >= 0 ? '+' : ''}
              {wauDelta} {phrase})
            </span>
          </span>
          <span title="Unique engineers active in the last 30 days of the window">
            MAU{' '}
            <span className="font-extrabold tabular-nums" style={{ color: OMS_COLORS.tealDark }}>
              {formatNumber(summary.mau)}
            </span>
          </span>
          <span title="Average daily uniques ÷ MAU">
            Stickiness{' '}
            <span className="font-extrabold tabular-nums" style={{ color: '#14606B' }}>
              {summary.stickiness === null ? '—' : formatPct(summary.stickiness)}
            </span>
            {summary.stickiness !== null && (
              <span className="ml-1 rounded-full px-1.5 py-0.5 text-[9px] font-extrabold uppercase" style={{ color: '#14606B', backgroundColor: tint('#14606B', 0.1) }}>
                {stickinessBand(summary.stickiness)}
              </span>
            )}
          </span>
        </div>
      </div>

      {loading && points.length === 0 ? (
        <div className="mt-3 h-56 animate-pulse rounded-xl bg-[rgba(11,59,46,0.06)]" />
      ) : (
        <Chart type="area" height={220} options={options} series={series} />
      )}

      <div className="mt-3 grid gap-4 border-t border-[rgba(11,59,46,0.10)] pt-3 lg:grid-cols-2">
        {/* Activation funnel */}
        <div aria-label="Activation funnel">
          <p className="text-[10px] font-bold uppercase tracking-[0.14em]" style={{ color: OMS_COLORS.caption }}>
            Activation funnel · all engineers
          </p>
          <ul className="mt-2 space-y-1.5">
            {funnel.map((stage, i) => {
              const conversion = stage.of > 0 ? Math.round((stage.value / stage.of) * 100) : null;
              const widthPct = funnel[0] && funnel[0].value > 0 ? (stage.value / funnel[0].value) * 100 : 0;
              return (
                <li key={stage.key}>
                  <div className="flex items-baseline justify-between gap-2 text-[11px] font-semibold">
                    <span style={{ color: OMS_COLORS.ink }}>{stage.label}</span>
                    <span className="tabular-nums" style={{ color: OMS_COLORS.caption }}>
                      <span className="font-extrabold" style={{ color: OMS_COLORS.tealDark }}>
                        {formatNumber(stage.value)}
                      </span>
                      {i > 0 && conversion !== null && ` · ${conversion}% of ${formatNumber(stage.of)}`}
                    </span>
                  </div>
                  <div className="mt-0.5 h-2 w-full overflow-hidden rounded-full" style={{ backgroundColor: 'rgba(11,59,46,0.08)' }}>
                    <div className="h-full rounded-full" style={{ width: `${widthPct}%`, backgroundColor: ADMIN.petrol }} />
                  </div>
                  {stage.note && (
                    <p className="mt-0.5 text-[9.5px] font-medium" style={{ color: OMS_COLORS.caption }}>
                      {stage.note}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
          <p className="mt-1.5 text-[9.5px] font-medium" style={{ color: OMS_COLORS.caption }}>
            "First login" isn't tracked yet — see the instrumentation appendix. Onboarding = account creation date.
          </p>
        </div>

        {/* Retention cohorts */}
        <div aria-label="Retention cohorts">
          <p className="text-[10px] font-bold uppercase tracking-[0.14em]" style={{ color: OMS_COLORS.caption }}>
            Retention · % of cohort active in week N after onboarding
          </p>
          {cohorts.length === 0 ? (
            <p className="py-6 text-center text-xs" style={{ color: OMS_COLORS.caption }}>
              No onboarding cohorts in range.
            </p>
          ) : (
            <div className="mt-2 overflow-x-auto">
              <table className="w-full border-collapse text-left">
                <thead>
                  <tr className="text-[9px] font-bold uppercase tracking-wide" style={{ color: OMS_COLORS.caption }}>
                    <th className="py-1 pr-2">Cohort</th>
                    <th className="py-1 pr-2 text-right">Size</th>
                    {cohorts[0]?.weeks.map((_, w) => (
                      <th key={w} className="px-1.5 py-1 text-center">
                        W{w}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {cohorts.map((row) => (
                    <tr key={row.cohort} className="border-t border-[rgba(11,59,46,0.08)]">
                      <td className="py-1 pr-2 text-[11px] font-bold" style={{ color: OMS_COLORS.tealDark }}>
                        {row.label}
                      </td>
                      <td className="py-1 pr-2 text-right text-[11px] font-semibold tabular-nums" style={{ color: OMS_COLORS.caption }}>
                        {row.size}
                      </td>
                      {row.weeks.map((value, w) => (
                        <CohortCell key={w} value={value} />
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="mt-1 text-[9.5px] font-medium" style={{ color: OMS_COLORS.caption }}>
                — means the week falls outside the fetched activity window, not zero retention.
              </p>
            </div>
          )}
        </div>
      </div>
    </motion.section>
  );
}
